import React, { useState, useMemo, useEffect } from 'react';
import ResponsiveDrawer from '../Drawer/drawer';
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import HomeIcon from '@mui/icons-material/Home';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import { useOrderDraft } from './OrderDraftContext';
import CartCard from '../../ReusableComponents/CartCard';
import { generateOrderPDF } from './PDFGenerator';
import { getPDF, sendMailToCustomer, sendMailToOffice, getCustomerById, formatDate } from '../../ReusableComponents/functions';
import { Bounce, ToastContainer, toast } from 'react-toastify';
import { formatPrice } from './pdf/utils/priceFormatter';

const OrderConfirmation = () => {
    const drawerWidth = 240;
    const navigate = useNavigate();
    const { draft, clearDraft } = useOrderDraft();
    const { selectedCustomer, cartItems, shippingDetails, signature } = draft;

    const orderNumber = shippingDetails.orderNumber || "874781";
    const orderDateStr = formatDate(shippingDetails.orderDate || new Date());

    const [emailCustomer, setEmailCustomer] = useState(false);
    const [emailOffice, setEmailOffice] = useState(false);

    // Send Mail feature state — mirrors PurchaseOrder.js behaviour
    const [showemail, setShowemail] = useState(false);
    const [customermail, setCustomermail] = useState('');
    const [customermaila, setCustomermaila] = useState('');
    const [customermailb, setCustomermailb] = useState('');
    const [customermailc, setCustomermailc] = useState('');
    const [pdfUrl, setPdfUrl] = useState('');
    const [loadingContacts, setLoadingContacts] = useState(false);

    // Sent-flags — once an email is sent, the checkbox becomes disabled (matches PO behaviour)
    const [ef, setEf] = useState(false);       // email_1 sent
    const [esec, setEsec] = useState(false);   // email_2 sent
    const [ethird, setEthird] = useState(false); // sc_email sent
    const [efour, setEfour] = useState(false);  // tc_email sent
    const [eadmin, setEadmin] = useState(false); // office sent

    // Fetch customer full contact list and PDF URL on mount
    useEffect(() => {
        const fetchData = async () => {
            // Fetch customer contacts
            if (selectedCustomer?.customerid) {
                setLoadingContacts(true);
                try {
                    const contacts = await getCustomerById(selectedCustomer.customerid);
                    if (contacts && contacts.length > 0) {
                        const c = contacts[0];
                        setCustomermail(c.email_1 || '');
                        setCustomermaila(c.email_2 || '');
                        setCustomermailb(c.sc_email || '');
                        setCustomermailc(c.tc_email || '');
                    }
                } catch (err) {
                    console.error('Error fetching customer contacts:', err);
                } finally {
                    setLoadingContacts(false);
                }
            }

            // Fetch PDF URL
            try {
                const pdfData = await getPDF(orderNumber);
                if (pdfData && pdfData.length > 0) {
                    setPdfUrl(pdfData[0].file);
                }
            } catch (err) {
                console.error('Error fetching PDF:', err);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showToast = (type, message) => {
        toast[type](message, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored',
            transition: Bounce,
        });
    };

    const handleSendMail = async (email, sentFlagSetter) => {
        try {
            const response = await sendMailToCustomer(
                email,
                pdfUrl,
                selectedCustomer?.StoreName,
                orderNumber,
                `${selectedCustomer?.firstname || ''} ${selectedCustomer?.lastname || ''}`.trim(),
                async () => {
                    const doc = await generateOrderPDF(draft, orderNumber, orderDateStr);
                    return doc.output('blob');
                }
            );
            if (response && response.success !== false) {
                showToast('success', 'Sent Successfully');
                sentFlagSetter(true);
            } else {
                showToast('error', 'Sending Failed');
            }
        } catch (err) {
            console.error(err);
            showToast('error', 'Sending Failed');
        }
    };

    const handleSendToOffice = async () => {
        try {
            const response = await sendMailToOffice(
                pdfUrl,
                selectedCustomer?.StoreName,
                orderNumber,
                async () => {
                    const doc = await generateOrderPDF(draft, orderNumber, orderDateStr);
                    return doc.output('blob');
                }
            );
            if (response && response.success !== false) {
                showToast('success', 'Sent Successfully');
                setEadmin(true);
            } else {
                showToast('error', 'Sending Failed');
            }
        } catch (err) {
            console.error(err);
            showToast('error', 'Sending Failed');
        }
    };

    const handleEmailCustomer = () => {
        setShowemail(prev => !prev);
        setEmailCustomer(prev => !prev);
    };

    const handleEmailOffice = () => {
        setEmailOffice(prev => !prev);
    };

    // Group items by SKU to render in summary list
    const groupedCart = useMemo(() => {
        const groups = {};
        cartItems.forEach(item => {
            if (!groups[item.sku]) {
                groups[item.sku] = {
                    sku: item.sku,
                    styleFactory: item.styleFactory,
                    color: item.color_on_box || item.Material1,
                    thumbnail: item.product_img,
                    Price1: item.Price1,
                    Price2: item.Price2,
                    Price3: item.Price3,
                    Size1: item.Size1,
                    Size2: item.Size2,
                    Size3: item.Size3,
                    items: []
                };
            }
            groups[item.sku].items.push(item);
        });
        return Object.values(groups);
    }, [cartItems]);

    // Calculate totals
    const grandTotals = useMemo(() => {
        let totalPairs = 0;
        let totalPrice = 0;
        cartItems.forEach(item => {
            totalPairs += item.unitQuantity;
            totalPrice += (item.unitQuantity * item.unitFinalPrice);
        });
        return { totalPairs, totalPrice };
    }, [cartItems]);

    const handleDownload = async () => {
        try {
            const doc = await generateOrderPDF(draft, orderNumber, orderDateStr);
            const cleanStoreName = (selectedCustomer?.StoreName || '').trim() || 'Store';
            doc.save(`${cleanStoreName}_${orderNumber}.pdf`);
        } catch (error) {
            console.error("Failed to generate download PDF: ", error);
        }
    };

    const handleHome = () => {
        clearDraft(); // Clears sessionStorage and resets state context
        navigate('/OrderHistory');
    };

    return (
        <div className="mx-4">
            <ResponsiveDrawer heading="Order Summary" />
            <Box
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                }}
                component="main"
            >
                <Toolbar />

                {/* Success Banner */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mt: 4,
                        mb: 3,
                        p: 2,
                        backgroundColor: 'rgba(76, 175, 80, 0.08)',
                        borderRadius: 2,
                        mx: 5
                    }}
                >
                    <CheckCircleIcon color="success" sx={{ fontSize: 50, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                        Order Placed Successfully!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        PO #{orderNumber} has been generated and saved.
                    </Typography>
                </Box>

                {/* Action Buttons bar */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mx: 5, mb: 4 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        sx={{ fontWeight: 'bold' }}
                    >
                        Download PDF
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<HomeIcon />}
                        onClick={handleHome}
                        sx={{ fontWeight: 'bold' }}
                    >
                        Home
                    </Button>
                </Box>

                <Box sx={{ px: 5, pb: 5 }}>
                    <Grid container spacing={4}>
                        {/* Left Details Panel */}
                        <Grid item xs={12}>
                            <Paper sx={{ p: 4, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)', mb: 4 }}>
                                <Grid container spacing={4}>

                                    {/* Left metadata info column */}
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, color: 'text.primary' }}>
                                            Order Details
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, color: 'text.secondary' }}>STORE NAME :</Typography>
                                                <Typography variant="body2" fontWeight="bold">{selectedCustomer?.StoreName}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, color: 'text.secondary' }}>PO# :</Typography>
                                                <Typography variant="body2" fontWeight="bold">{orderNumber}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, color: 'text.secondary' }}>CUSTOMER ID :</Typography>
                                                <Typography variant="body2" fontWeight="500" sx={{ wordBreak: 'break-all' }}>{selectedCustomer?.customerid}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, color: 'text.secondary' }}>BUYER NAME :</Typography>
                                                <Typography variant="body2" fontWeight="bold">{`${selectedCustomer?.firstname || ""} ${selectedCustomer?.lastname || ""}`.trim()}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, color: 'text.secondary' }}>CUSTOMER EMAIL :</Typography>
                                                <Typography variant="body2" fontWeight="500">{selectedCustomer?.email_1 || selectedCustomer?.email_2 || "None Provided"}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, color: 'text.secondary' }}>PHONE NUMBER :</Typography>
                                                <Typography variant="body2" fontWeight="500">{selectedCustomer?.cell_1 || "None Provided"}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, color: 'text.secondary' }}>ORDER DATE :</Typography>
                                                <Typography variant="body2" fontWeight="500">{orderDateStr}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, color: 'text.secondary' }}>START DATE :</Typography>
                                                <Typography variant="body2" fontWeight="500">{shippingDetails.startDate}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, color: 'text.secondary' }}>CANCEL DATE :</Typography>
                                                <Typography variant="body2" fontWeight="500">{shippingDetails.cancelDate}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, color: 'text.secondary' }}>PAYMENT TERM :</Typography>
                                                <Typography variant="body2" fontWeight="bold" color="primary">{shippingDetails.paymentTerm || "None Selected"}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    {/* Right addresses column */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {/* Shipping Address Display */}
                                            <Box>
                                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: 'primary.main' }}>
                                                    SHIPPING ADDRESS
                                                </Typography>
                                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'primary.light' }}>
                                                    <Typography variant="body2" fontWeight="bold">{shippingDetails.shippingAddress.storeName}</Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.5 }}>{shippingDetails.shippingAddress.address1}</Typography>
                                                    {shippingDetails.shippingAddress.address2 && (
                                                        <Typography variant="body2">{shippingDetails.shippingAddress.address2}</Typography>
                                                    )}
                                                    <Typography variant="body2">
                                                        {[
                                                            shippingDetails.shippingAddress.city,
                                                            shippingDetails.shippingAddress.state,
                                                            shippingDetails.shippingAddress.postcode
                                                        ].filter(Boolean).join(", ")}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="500" sx={{ mt: 0.5 }}>{shippingDetails.shippingAddress.country}</Typography>
                                                </Box>
                                            </Box>

                                            {/* Billing Address Display */}
                                            <Box>
                                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: 'primary.main' }}>
                                                    BILLING ADDRESS
                                                </Typography>
                                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'primary.light' }}>
                                                    <Typography variant="body2" sx={{ mt: 0.5 }}>{shippingDetails.billingAddress.address1}</Typography>
                                                    {shippingDetails.billingAddress.address2 && (
                                                        <Typography variant="body2">{shippingDetails.billingAddress.address2}</Typography>
                                                    )}
                                                    <Typography variant="body2">
                                                        {[
                                                            shippingDetails.billingAddress.city,
                                                            shippingDetails.billingAddress.state,
                                                            shippingDetails.billingAddress.postcode
                                                        ].filter(Boolean).join(", ")}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="500" sx={{ mt: 0.5 }}>{shippingDetails.billingAddress.country}</Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>

                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Product Items Summary List */}
                        <Grid item xs={12}>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: 'text.primary' }}>
                                Ordered Products
                            </Typography>
                            {groupedCart.map(group => (
                                <CartCard
                                    key={group.sku}
                                    group={group}
                                    onDelete={() => { }}
                                    readOnly={true}
                                />
                            ))}
                        </Grid>

                        {/* Signature and Totals Summary Panel */}
                        <Grid item xs={12}>
                            <Paper sx={{ p: 4, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                                <Grid container spacing={4} alignItems="center">
                                    {/* Signature Display */}
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1.5 }}>
                                            AUTHORIZED SIGNATURE
                                        </Typography>
                                        {signature ? (
                                            <Box
                                                sx={{
                                                    width: 240,
                                                    height: 90,
                                                    backgroundColor: '#f2f6f8',
                                                    borderRadius: 1,
                                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                                    p: 0.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={signature}
                                                    alt="Authorized Customer Signature"
                                                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                />
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                                No Signature Captured
                                            </Typography>
                                        )}
                                    </Grid>

                                    {/* Final Totals & Email Options */}
                                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 2 }}>
                                        <Box sx={{ width: '100%', maxWidth: 280 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="body1" fontWeight="bold" color="text.secondary">Quantity:</Typography>
                                                <Typography variant="body1" fontWeight="bold">{grandTotals.totalPairs} Pairs</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="h6" fontWeight="bold" color="primary">Grand Total:</Typography>
                                                <Typography variant="h6" fontWeight="bold" color="primary">${formatPrice(grandTotals.totalPrice.toFixed(2))}</Typography>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ width: '100%', my: 1 }} />

                                        {/* Email checkboxes — Send Mail feature matching PurchaseOrder */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {/* Email To Customer toggle */}
                                            <FormGroup className="d-flex">
                                                <FormControlLabel
                                                    control={<Checkbox checked={emailCustomer} onChange={handleEmailCustomer} color="error" size="small" />}
                                                    label={<Typography variant="body2" fontWeight="bold" color="error">Email To Customer</Typography>}
                                                />
                                            </FormGroup>

                                            {/* Expandable customer email list — shown when toggle is on */}
                                            {showemail && (
                                                <Box sx={{ pl: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {loadingContacts ? (
                                                        <CircularProgress size={18} />
                                                    ) : (
                                                        <>
                                                            {/* email_1 */}
                                                            {!customermail ? (
                                                                <Typography variant="caption" color="text.disabled">NO Email Found!</Typography>
                                                            ) : (
                                                                <FormGroup className="d-flex">
                                                                    <FormControlLabel
                                                                        control={<Checkbox size="small" defaultChecked={ef} disabled={ef} />}
                                                                        label={<Typography variant="caption">{customermail}</Typography>}
                                                                        value={customermail}
                                                                        onChange={!ef ? () => handleSendMail(customermail, setEf) : undefined}
                                                                    />
                                                                </FormGroup>
                                                            )}

                                                            {/* email_2 */}
                                                            {!customermaila ? (
                                                                <Typography variant="caption" color="text.disabled">NO Email Found!</Typography>
                                                            ) : (
                                                                <FormGroup className="d-flex">
                                                                    <FormControlLabel
                                                                        control={<Checkbox size="small" defaultChecked={esec} disabled={esec} />}
                                                                        label={<Typography variant="caption">{customermaila}</Typography>}
                                                                        value={customermaila}
                                                                        onChange={!esec ? () => handleSendMail(customermaila, setEsec) : undefined}
                                                                    />
                                                                </FormGroup>
                                                            )}

                                                            {/* sc_email */}
                                                            {!customermailb ? (
                                                                <Typography variant="caption" color="text.disabled">NO Email Found!</Typography>
                                                            ) : (
                                                                <FormGroup className="d-flex">
                                                                    <FormControlLabel
                                                                        control={<Checkbox size="small" defaultChecked={ethird} disabled={ethird} />}
                                                                        label={<Typography variant="caption">{customermailb}</Typography>}
                                                                        value={customermailb}
                                                                        onChange={!ethird ? () => handleSendMail(customermailb, setEthird) : undefined}
                                                                    />
                                                                </FormGroup>
                                                            )}

                                                            {/* tc_email */}
                                                            {!customermailc ? (
                                                                <Typography variant="caption" color="text.disabled">NO Email Found!</Typography>
                                                            ) : (
                                                                <FormGroup className="d-flex">
                                                                    <FormControlLabel
                                                                        control={<Checkbox size="small" defaultChecked={efour} disabled={efour} />}
                                                                        label={<Typography variant="caption">{customermailc}</Typography>}
                                                                        value={customermailc}
                                                                        onChange={!efour ? () => handleSendMail(customermailc, setEfour) : undefined}
                                                                    />
                                                                </FormGroup>
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            )}

                                            {/* Email To Office */}
                                            <FormGroup className="d-flex">
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={emailOffice || eadmin}
                                                            disabled={eadmin}
                                                            color="primary"
                                                            size="small"
                                                            onChange={!eadmin ? handleSendToOffice : undefined}
                                                        />
                                                    }
                                                    label={<Typography variant="body2" fontWeight="bold" color="primary">Email To Office</Typography>}
                                                />
                                            </FormGroup>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <ToastContainer />
        </div>
    );
};

export default OrderConfirmation;
